import { Button, formatDateTime, PageEmpty, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { answerText, type DeliveryRecord, deliveryHref, deliveryQuery, isFileKind, isLive } from "../../deliveries";
import { messageFor } from "../../error-copy";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import { ProofDialog } from "../orders/proof";
import page from "../orders/record.module.css";
import { DeliveryActions } from "./actions";
import { ChargesSection } from "./charges";
import { DeliverToCard, IncidentsCard, TimelineCard } from "./side";
import { Tracker } from "./tracker";
import { CrewSection, RouteSection } from "./trip";

/**
 * One delivery (P8-48, `Console / Delivery`), under its order: where it is on the trip, the route,
 * truck and crew, what it charges and costs, the proof, and on the side where it goes, its
 * incidents and its timeline. Dispatch's actions work here too.
 */
export function DeliveryPage(): ReactNode {
  const { orderId, deliveryId } = useParams({ from: "/console/orders/$orderId/deliveries/$deliveryId" });
  const { data: me } = useSuspenseQuery(meQuery);
  // refreshed every 10 s while the truck is on its way; a finished delivery stops polling (P8-51)
  const delivery = useQuery({
    ...deliveryQuery(deliveryId),
    refetchInterval: (query) => (query.state.data && isLive(query.state.data.status) ? 10_000 : false),
  });
  const d = delivery.data;
  useRecordCrumb(d ? `${d.order.order_number} › ${d.delivery_number}` : null);

  if (delivery.status === "pending") return <PageLoading label="Loading the delivery" />;
  if (delivery.status === "error") {
    const error = errorOf(delivery.error);
    if (error.code === "DELIVERY_NOT_FOUND") {
      return <PageEmpty title="No such delivery" description="It may be in another company, or the link is wrong." />;
    }
    return <PageError message={messageFor(error)} onRetry={() => delivery.refetch()} />;
  }
  if (!d || d.order.id !== orderId) {
    return <PageEmpty title="No such delivery" description="It isn't on this order." />;
  }

  const canAct = (me?.permissions ?? []).includes("deliveries.dispatch");
  const crew = [d.assignment?.truck?.plate_number, d.assignment?.driver?.name].filter(Boolean).join(" · ");
  return (
    <>
      <PageHeader
        title={d.delivery_number}
        description={[
          d.items.map((i) => i.name).join(", ") || null,
          `for ${d.order.business?.name ?? d.order.customer.name}`,
          d.order.order_number,
          d.geofence ? `${d.geofence.name} zone` : null,
          crew || null,
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={canAct ? <DeliveryActions delivery={d} /> : undefined}
      />
      <Stopped delivery={d} orderId={orderId} />
      <Tracker delivery={d} />
      <div className={page.columns}>
        <div className={page.main}>
          <RouteSection delivery={d} />
          <CrewSection delivery={d} />
          <ChargesSection delivery={d} />
          <ProofSection delivery={d} />
        </div>
        <aside className={page.side}>
          <DeliverToCard delivery={d} />
          <IncidentsCard delivery={d} canAct={canAct} />
          <TimelineCard delivery={d} />
        </aside>
      </div>
    </>
  );
}

/** Why a delivery off the main line stopped, and where it went if it was rerouted. */
function Stopped({ delivery: d, orderId }: { delivery: DeliveryRecord; orderId: string }) {
  const format = useCompanyFormat();
  const reason = d.events[0]?.reason;
  // a load sent to another client is on another order (P8-50)
  const link = (to: { id: string; delivery_number: string; order_id?: string }) => (
    <RouterLink href={deliveryHref(to.order_id ?? orderId, to.id)} className={page.link}>
      {to.delivery_number}
    </RouterLink>
  );
  if (d.status === "rerouted") {
    return (
      <Alert tone="info" title="Rerouted">
        {d.load_to ? <>Its load went to {link(d.load_to)}, another client's delivery. </> : null}
        {d.replaced_by ? <>It goes on as {link(d.replaced_by)}. </> : null}
        {d.load_to && !d.replaced_by ? <>The customer gets no new delivery. </> : null}
        {reason}
      </Alert>
    );
  }
  if (d.status === "cancelled" || d.status === "failed") {
    return (
      <Alert tone={d.status === "failed" ? "danger" : "info"} title={d.status === "failed" ? "It failed" : "Cancelled"}>
        {[d.cancelled_at ? formatDateTime(d.cancelled_at, format) : null, reason].filter(Boolean).join(" · ")}
      </Alert>
    );
  }
  if (d.status === "suspended") {
    return (
      <Alert tone="warning" title="Suspended at the customer">
        {reason ?? "It waits until it's resumed or rerouted."}
      </Alert>
    );
  }
  if (d.load_from) {
    return (
      <Alert tone="info" title="It took over another load">
        The truck was rerouted here with the load of {link(d.load_from)}.
      </Alert>
    );
  }
  if (d.rerouted_from) {
    return (
      <Alert tone="info" title="A reroute">
        It replaces {link(d.rerouted_from)}.
      </Alert>
    );
  }
  return null;
}

/** The proof of delivery: a summary here, the photos and signatures in the dialog. */
function ProofSection({ delivery: d }: { delivery: DeliveryRecord }) {
  const [open, setOpen] = useState(false);
  const p = d.proof;
  const words = p?.checklist.filter((a) => !isFileKind(a.kind)) ?? [];
  const files = p?.checklist.filter((a) => isFileKind(a.kind) && a.file_id) ?? [];
  return (
    <section className={page.section} aria-label="Proof of delivery">
      <h2 className={page.sectionTitle}>Proof of delivery</h2>
      <div className={page.card}>
        {p ? (
          <>
            <span>
              {p.received_by_name ? `Received by ${p.received_by_name}. ` : ""}
              {words.map((a) => `${a.label}: ${answerText(a)}.`).join(" ")}
            </span>
            {files.length ? (
              <div>
                <Button variant="secondary" onClick={() => setOpen(true)}>
                  See the {files.map((a) => a.label.toLowerCase()).join(" and ")}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <p className={page.muted}>
            Shows once it's delivered: who received it, whether the quantity and quality were right, the photo and the
            signature.
          </p>
        )}
      </div>
      {open ? <ProofDialog orderId={d.order.id} delivery={d} onClose={() => setOpen(false)} /> : null}
    </section>
  );
}
